import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createRoute, redirect } from "@tanstack/react-router";
import { Button } from "@carry/ui";
import { Route as RootRoute } from "./__root";
import { useAdminApi } from "../providers/api-context";
import type { RouterContext } from "../router-context";
import { formatDateTime } from "../lib/format";
import { lineSendStatusLabel, notificationTypeLabel } from "@carry/contract";

const PAGE_SIZE = 50;

export function requireLineLogsRoute({ context }: { context: RouterContext }) {
  if (context.authLoading) return;
  if (!context.session) throw redirect({ to: "/auth/login" });
  if (context.session.user.role !== "admin" && context.session.user.role !== "office_staff") {
    throw redirect({ to: "/" });
  }
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/line-logs",
  beforeLoad: requireLineLogsRoute,
  component: LineLogsPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: "var(--bg-raised)",
        border: "0.5px solid var(--rule)",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "0.5px solid var(--rule-soft)",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function EmptyRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
        ログはありません
      </td>
    </tr>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div aria-busy="true" style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
      {label}を読み込み中です
    </div>
  );
}

function ErrorState({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div role="alert" style={{ padding: 24, textAlign: "center" }}>
      <div style={{ marginBottom: 12, color: "var(--danger)" }}>{label}を取得できませんでした</div>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        再試行
      </Button>
    </div>
  );
}

export function LineLogsPage() {
  const api = useAdminApi();
  const stats = useQuery({
    queryKey: ["linePushStats"],
    queryFn: () => api.line.pushStats({}),
  });
  const sendLogs = useInfiniteQuery({
    queryKey: ["lineSendLogs"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.line.sendLogs({ limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: (lastPage) => lastPage.pagination.next_cursor ?? undefined,
  });
  const webhookLogs = useInfiniteQuery({
    queryKey: ["lineWebhookLogs"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.line.webhookLogs({ limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: (lastPage) => lastPage.pagination.next_cursor ?? undefined,
  });

  const statsData = stats.data?.data;
  const sendRows = sendLogs.data?.pages.flatMap((page) => page.data) ?? [];
  const webhookRows = webhookLogs.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div style={{ margin: -24 }}>
      <div
        className="cp-pageheader"
        style={{
          padding: "12px 32px",
          borderBottom: "0.5px solid var(--rule-softer)",
          background: "var(--bg)",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>LINEログ</h1>
        <div style={{ marginTop: 3, fontSize: 11, color: "var(--ink-3)" }}>
          LINE Pushの送信数・送信結果とWebhook受信結果を閲覧します
        </div>
      </div>

      <div style={{ padding: "24px 32px 32px", display: "grid", gap: 20 }}>
        <Section title="送信数統計">
          {stats.isPending ? (
            <LoadingState label="送信数統計" />
          ) : stats.isError || !statsData ? (
            <ErrorState label="送信数統計" onRetry={() => void stats.refetch()} />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              }}
            >
              {[
                ["対象月", statsData.month],
                ["送信数", statsData.total_count.toLocaleString("ja-JP")],
                ["月間上限", statsData.limit.toLocaleString("ja-JP")],
                ["残り送信枠", statsData.remaining.toLocaleString("ja-JP")],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{ padding: 16, borderRight: "0.5px solid var(--rule-soft)" }}
                >
                  <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 6 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 600, fontFamily: "var(--f-mono)" }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="送信ログ">
          {sendLogs.isPending ? (
            <LoadingState label="送信ログ" />
          ) : sendLogs.isError && !sendLogs.data ? (
            <ErrorState label="送信ログ" onRetry={() => void sendLogs.refetch()} />
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="tbl" style={{ minWidth: 720, fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>記録日時</th>
                      <th>種別</th>
                      <th>タイトル</th>
                      <th>送信先</th>
                      <th>状態</th>
                      <th>失敗理由</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sendRows.length === 0 && <EmptyRow colSpan={6} />}
                    {sendRows.map((log, index) => (
                      <tr key={`${log.created_at}-${log.notification_type}-${index}`}>
                        <td>{formatDateTime(log.created_at)}</td>
                        <td>{notificationTypeLabel(log.notification_type)}</td>
                        <td>{log.title}</td>
                        <td>{log.recipient_name ?? "—"}</td>
                        <td>{lineSendStatusLabel(log.status)}</td>
                        <td>{log.fail_reason ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sendLogs.isFetchNextPageError && (
                <ErrorState label="送信ログの続き" onRetry={() => void sendLogs.fetchNextPage()} />
              )}
              {sendLogs.hasNextPage && !sendLogs.isFetchNextPageError && (
                <div style={{ padding: 12, textAlign: "center" }}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label="送信ログの次を読み込む"
                    disabled={sendLogs.isFetchingNextPage}
                    onClick={() => void sendLogs.fetchNextPage()}
                  >
                    次を読み込む
                  </Button>
                </div>
              )}
            </>
          )}
        </Section>

        <Section title="Webhookログ">
          {webhookLogs.isPending ? (
            <LoadingState label="Webhookログ" />
          ) : webhookLogs.isError && !webhookLogs.data ? (
            <ErrorState label="Webhookログ" onRetry={() => void webhookLogs.refetch()} />
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="tbl" style={{ minWidth: 640, fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>受信日時</th>
                      <th>イベント</th>
                      <th>処理結果</th>
                      <th>エラー</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhookRows.length === 0 && <EmptyRow colSpan={4} />}
                    {webhookRows.map((log, index) => (
                      <tr key={`${log.received_at}-${log.event_type}-${index}`}>
                        <td>{formatDateTime(log.received_at)}</td>
                        <td>{log.event_type}</td>
                        <td>{log.status === "success" ? "成功" : "失敗"}</td>
                        <td>{log.error ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {webhookLogs.isFetchNextPageError && (
                <ErrorState
                  label="Webhookログの続き"
                  onRetry={() => void webhookLogs.fetchNextPage()}
                />
              )}
              {webhookLogs.hasNextPage && !webhookLogs.isFetchNextPageError && (
                <div style={{ padding: 12, textAlign: "center" }}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label="Webhookログの次を読み込む"
                    disabled={webhookLogs.isFetchingNextPage}
                    onClick={() => void webhookLogs.fetchNextPage()}
                  >
                    次を読み込む
                  </Button>
                </div>
              )}
            </>
          )}
        </Section>
      </div>
    </div>
  );
}
